import { Paper, Stack, Text, ThemeIcon } from "@mantine/core"
import {
  DefaultList,
  DefaultListProps,
} from "../../../../../../-components/default-list"
import { ReactionPillGroup } from "../ReactionPillGroup"
import { PostHeader } from "./PostHeader"
import { useGetAuthenticatedUser } from "../../../../-components/use-authenticated-user"
import { IconPencil, IconTrash } from "@tabler/icons-react"
import { DottedMenu } from "../../../../../../-components/dotted-menu"
import { useTranslation } from "react-i18next"
import { EditFeedPostModal } from "./EditFeedPostModal"
import { useDisclosure } from "@mantine/hooks"
import {
  getGetFeedPostsForUserQueryKey,
  getGetFeedPostsQueryKey,
  useDeletePost,
} from "@/orval/gen"
import { ChatPostDto } from "@/orval/gen/model"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useBatchedSimpleUser } from "@/-dataloaders/simple-users"
import { stripHtml } from "@/utils/stripHtml"

interface CommentItemProps {
  topicPost: ChatPostDto
}

function CommentItem({ topicPost: comment }: CommentItemProps) {
  const { data: author } = useBatchedSimpleUser(comment.userId)

  const currentUserId = useGetAuthenticatedUser().data.userId

  const { t } = useTranslation()

  const queryClient = useQueryClient()

  const { mutateAsync: deletePost } = useDeletePost({
    mutation: {
      onError: showErrorNotification,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: getGetFeedPostsQueryKey(),
        })
        queryClient.invalidateQueries({
          queryKey: getGetFeedPostsForUserQueryKey(),
        })
      },
    },
  })

  const [opened, { open, close }] = useDisclosure()

  return (
    <Paper
      onClick={(evt) => evt.stopPropagation()}
      shadow="sm"
      style={{ cursor: "default" }}
      p="sm"
      withBorder
    >
      <Stack>
        <PostHeader
          author={author}
          timeStamp={comment.timeStamp}
          rightSection={
            currentUserId === author?.userId ? (
              <DottedMenu
                items={[
                  {
                    leftSection: (
                      <ThemeIcon size="xs" variant="subtle">
                        <IconPencil />
                      </ThemeIcon>
                    ),
                    children: t("Edit"),
                    onClick: open,
                  },
                  {
                    leftSection: (
                      <ThemeIcon size="xs" c="red" variant="subtle">
                        <IconTrash />
                      </ThemeIcon>
                    ),
                    children: t("Delete"),
                    onClick: async () =>
                      await deletePost({
                        params: {
                          postId: comment.chatPostId,
                        },
                      }),
                  },
                ]}
              />
            ) : null
          }
        />
        <Text style={{ whiteSpace: "pre-wrap" }}>
          {stripHtml(comment.postText)}
        </Text>
        <ReactionPillGroup
          messageId={comment.chatPostId}
          reactionCount={comment.reactions.length}
          userReacted={comment.reactions.some((id) => id === currentUserId)}
        />
      </Stack>
      <EditFeedPostModal
        isComment
        chatPostDto={comment}
        opened={opened}
        onClose={close}
      />
    </Paper>
  )
}

interface FeedPostCommentListProps
  extends CommentItemProps,
    Omit<DefaultListProps<ChatPostDto, false>, "data" | "renderItem"> {}

export function FeedPostCommentList({
  topicPost,
  ...props
}: FeedPostCommentListProps) {
  return (
    <DefaultList
      p="xs"
      gap="xs"
      style={{ borderRadius: 5, overflow: "auto" }}
      data={(topicPost.comments as ChatPostDto[]) ?? []}
      divider
      renderItem={({ entity: comment }) => <CommentItem topicPost={comment} />}
      {...props}
      startAtBottom
    />
  )
}
